import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { apiClient } from "@/api/client";
import { useSuspenseQuery, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { SensorKit, SensorKitModel } from "@/sensorKits/sensorKit";

export interface SensorKitFilters {
  config_model?: SensorKitModel | "";
  config_version?: string;
  config_versions?: string[];
  has_current_installation?: boolean;
  is_offline?: boolean;
  has_missing_sensor_data?: boolean;
  has_out_of_spec_sensor_data?: boolean;
  page?: number;
  pageSize?: number;
}

// Shared URL builder for sensor kits
const buildSensorKitsUrl = (filters: Partial<SensorKitFilters>) => {
  let url = `/sensor-kits/`;
  const params = new URLSearchParams();

  if (filters?.config_model) {
    params.append('config_model', filters.config_model);
  }

  if (filters?.config_version) {
    params.append('config_version', filters.config_version);
  }

  if (filters?.config_versions && filters.config_versions.length > 0) {
    filters.config_versions.forEach(version => {
      params.append('config_version', version);
    });
  }

  if (filters?.has_current_installation !== undefined) {
    params.append('has_current_installation', filters.has_current_installation.toString());
  }

  if (filters?.is_offline !== undefined) {
    params.append('is_offline', filters.is_offline.toString());
  }

  if (filters?.has_missing_sensor_data !== undefined) {
    params.append('has_missing_sensor_data', filters.has_missing_sensor_data.toString());
  }

  if (filters?.has_out_of_spec_sensor_data !== undefined) {
    params.append('has_out_of_spec_sensor_data', filters.has_out_of_spec_sensor_data.toString());
  }

  if (filters?.page !== undefined) {
    params.append('page', filters.page.toString());
  }

  if (filters?.pageSize !== undefined) {
    params.append('page_size', filters.pageSize.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return url;
};

export const sensorKits = createQueryKeys('sensor-kits', {
  detail: (
    serialNumber: string,
  ) => ({
    queryKey: [serialNumber],
    queryFn: async () => {
      return await get<SensorKit>(`sensor-kits/${serialNumber}/`);
    },
  }),
  onlineStatus: (
    serialNumber: string,
  ) => ({
    queryKey: [serialNumber, 'online-status'],
    queryFn: async () => {
      return await get<{ is_online: boolean }>(`sensor-kits/${serialNumber}/online-in-balena/`);
    },
  }),
  dataExists: (
    serialNumber: string,
    start: string,
    stop: string,
  ) => ({
    queryKey: [serialNumber, 'data-exists', start, stop],
    queryFn: async () => {
      const response = await apiClient.post<{ exists: boolean }>(
        `sensor-kits/${serialNumber}/data-exists/`,
        { start, stop }
      );
      return response.data;
    },
  }),
  list: (filters?: SensorKitFilters) => ({
    queryKey: ["list", filters],
    queryFn: async () => {
      const url = buildSensorKitsUrl(filters || {});
      return await get<PaginatedResponse<SensorKit>>(url);
    },
  }),
  infinite: (filters?: Omit<SensorKitFilters, 'page' | 'pageSize'>) => ({
    queryKey: ["infinite", filters],
  })
});

export const useSensorKitBySerialNumber = (serialNumber: string) => {
  return useSuspenseQuery(sensorKits.detail(serialNumber))
}

export const useSensorKitOnlineStatus = (serialNumber: string) => {
  return useSuspenseQuery(sensorKits.onlineStatus(serialNumber))
}

export const useSensorKitDataExists = (serialNumber: string, start: string, stop: string) => {
  return useQuery(sensorKits.dataExists(serialNumber, start, stop))
}

export const useSensorKits = (filters?: SensorKitFilters) => {
  return useSuspenseQuery(sensorKits.list(filters))
}

// Infinite query for sensor kits
export const useSensorKitsInfinite = (filters?: Omit<SensorKitFilters, 'page' | 'pageSize'>) => {
  return useInfiniteQuery({
    queryKey: sensorKits.infinite(filters).queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const url = buildSensorKitsUrl({
        ...filters,
        page: pageParam as number,
        pageSize: 10
      });
      return await get<PaginatedResponse<SensorKit>>(url);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<SensorKit>, allPages) => {
      const totalPages = Math.ceil(lastPage.count / 10);
      const currentPage = allPages.length;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
}
